import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { Spin, Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Hosts } from '~helpers/environments';
import { getItem } from '~helpers/storage';
import * as commissionActions from '~actions/properties/commission';
import OldCommissionFormModal from '~components/commission/commission-form-modal';
import CommissionList from '~components/commission/commission-list';
import CommissionEmpty from '~components/commission/commission-empty';
import CreateCommission from '~components/commission/create-commission';
import LeaveAlert from '~components/leave-alert';
import Header from '~components/property-header';
import { communicationStatus, platformEntity, entityAction, commissionLevel } from '~constants';
import { landlordDetailTabs } from '~constants/landlord';
import { isInternalRole } from '~helpers/auth';
import authControl from '~components/auth-control';
import generatePath from '~settings/routing';

const mapStateToProps = state => {
  const data = state.dashboard.commissionReducer?.toJS();

  return {
    property: data.propertyCommissionTiers,
    getPropertyStatus: data.communication.propertyCommissionTiers.status,
    propertyCommissionTiers: data.propertyCommissionTiers?.allCommissionTiers?.propertyLevel ?? [],
    landlordCommissionTiers: data.propertyCommissionTiers?.allCommissionTiers?.landlordLevel ?? [],
    platformCommissionTiers: data.propertyCommissionTiers?.allCommissionTiers?.platformLevel ?? [],
    createStatus: data.communication.create.status,
    updateStatus: data.communication.update.status,
    deleteStatus: data.communication.delete.status,
    bulkCreateStatus: data.communication.bulkCreate.status,
    landlordProperties: data.landlordProperties,
    commissionRooms: data.propertyCommissionTiers.unitTypes,
  };
};

const mapDispatchToProps = dispatch => ({
  getPropertyCommissionTiers: (id, successCallback) => {
    dispatch(commissionActions.getPropertyCommissionTiers(id, successCallback));
  },
  getLandlordProperties: (slug, successCallback) => {
    dispatch(commissionActions.getLandlordProperties(slug, successCallback));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
@withRouter
@authControl(platformEntity.COMMISSION_COMISSION_TIERS, entityAction.READ, props =>
  generatePath('property.homepage', { propertySlug: props.match.params.propertySlug }),
)
export default class PropertyCommission extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      commissionCategory: null,
      modalType: 'addNew',
      correlationId: null,
      showCommissionModal: false,
      triggerPoint: 'CREATED',
    };
  }

  componentDidMount() {
    this.props.getPropertyCommissionTiers(
      decodeURIComponent(this.props.match.params.propertySlug),
      () => {
        if (!this.props.property) {
          this.props.history.push(generatePath('properties', {}));
        }

        if (this.props.property && this.props.property.landlord) {
          this.props.getLandlordProperties(this.props.property.landlord.slug);
        }
      },
    );
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.createStatus === communicationStatus.IDLE &&
        prevProps.createStatus === communicationStatus.FETCHING) ||
      (this.props.updateStatus === communicationStatus.IDLE &&
        prevProps.updateStatus === communicationStatus.FETCHING) ||
      (this.props.deleteStatus === communicationStatus.IDLE &&
        prevProps.deleteStatus === communicationStatus.FETCHING) ||
      (this.props.bulkCreateStatus === communicationStatus.IDLE &&
        prevProps.bulkCreateStatus === communicationStatus.FETCHING)
    ) {
      this.props.getPropertyCommissionTiers(
        decodeURIComponent(this.props.match.params.propertySlug),
      );
    }
  }

  generatePropertyUrl = property => {
    const path = `${
      property.city && property.city.country && property.city.country.slug
        ? property.city.country.slug
        : ''
    }/${property.city && property.city.slug ? property.city.slug : ''}/p/${property.slug}`;

    return `${Hosts.propertyUrl}${path}`;
  };

  getFilters = () => {
    const filters = getItem('cms_properties_list_filters');
    const result = {};
    if (filters) {
      Object.keys(filters).map(key => {
        if (filters[key]) {
          result[key] = filters[key];
        }
        return true;
      });
    }
    return result;
  };

  changeCommissionCategory = type => {
    this.setState({
      commissionCategory: type,
    });
  };

  createCommission = () => {
    this.setState({
      showCommissionModal: !this.state.showCommissionModal,
    });
  };

  openModal = (type, commissionTier) => {
    this.setState({
      showCommissionModal: true,
      modalType: type,
      triggerPoint: commissionTier?.triggerPoint || 'CREATED',
      correlationId: commissionTier ? commissionTier.id : null,
      commissionCategory:
        (type === 'addNew' || type === 'copy') && commissionTier ? commissionTier.category : null,
    });
  };

  handleGoToCheck = () => {
    this.props.history.push(
      generatePath(
        'landlord',
        {
          landlordSlug: this.props.property.landlord.slug,
        },
        {
          tab: landlordDetailTabs.COMMISSION,
        },
      ),
    );
  };

  render() {
    const {
      t,
      property,
      getPropertyStatus,
      propertyCommissionTiers,
      landlordCommissionTiers,
      platformCommissionTiers,
      landlordProperties,
      commissionRooms,
    } = this.props;

    return (
      <div className="property-commission">
        <LeaveAlert history={this.props.history} t={t} when={this.state.showCommissionModal} />
        <Choose>
          <When condition={getPropertyStatus !== communicationStatus.IDLE}>
            <div className="property-edit__loading">
              <Spin />
            </div>
          </When>
          <Otherwise>
            <Header t={t} property={property} type="commission" />

            <div className="property-commission__page">
              <If
                condition={
                  propertyCommissionTiers?.length === 0 &&
                  landlordCommissionTiers?.length === 0 &&
                  platformCommissionTiers?.length === 0
                }
              >
                <If condition={isInternalRole()}>
                  <div className="property-commission__content-create">
                    <h3 className="property-commission__content-title">
                      {t('cms.property.commission.create.title.text')}
                    </h3>
                    <CreateCommission
                      t={t}
                      commissionLevel={commissionLevel.PROPERTY}
                      commissionCategory={this.state.commissionCategory}
                      changeCommissionCategory={this.changeCommissionCategory}
                      createCommission={this.createCommission}
                    />
                  </div>
                </If>
                <If condition={!isInternalRole()}>
                  <CommissionEmpty t={t} />
                </If>
              </If>

              <If
                condition={
                  propertyCommissionTiers?.length > 0 ||
                  landlordCommissionTiers?.length > 0 ||
                  platformCommissionTiers?.length > 0
                }
              >
                <h3 className="property-commission__section-title">
                  {t('cms.property.commission.section.property_level.title')}
                </h3>

                <CommissionList
                  t={t}
                  commissionLevel={commissionLevel.PROPERTY}
                  currency={property.currency}
                  commissionTier={propertyCommissionTiers}
                  showOperations={true}
                  property={property}
                  openModal={this.openModal}
                />
              </If>

              <If condition={landlordCommissionTiers?.length > 0}>
                <h3 className="property-commission__section-title">Landlord Level</h3>

                <CommissionList
                  t={t}
                  currency={property.currency}
                  openModal={this.openModal}
                  commissionTier={landlordCommissionTiers}
                  showCreateCard={false}
                  showOperations={false}
                />

                <div className="property-commission__section-tooltip">
                  <ExclamationCircleOutlined style={{ fontWeight: 'bold' }} />
                  <span>{t('cms.property.commission.section.landlord_level.tooltip')}</span>

                  <Button type="link" onClick={this.handleGoToCheck}>
                    {t('cms.property.commission.section.landlord_level.check_button')}
                  </Button>
                </div>
              </If>
              <If condition={platformCommissionTiers?.length > 0}>
                <h3 className="property-commission__section-title">{'Platform Level'}</h3>

                <CommissionList
                  t={t}
                  commissionLevel={commissionLevel.PLATFORM}
                  currency={property.currency}
                  commissionTier={platformCommissionTiers}
                  showOperations={false}
                  showCreateCard={false}
                  property={property}
                  openModal={this.openModal}
                />
              </If>
              <If condition={this.state.showCommissionModal}>
                <OldCommissionFormModal
                  activeModal
                  t={t}
                  property={property}
                  triggerPoint={this.state.triggerPoint}
                  commissionLevel={commissionLevel.PROPERTY}
                  commissionTiers={[...propertyCommissionTiers, ...landlordCommissionTiers]}
                  onClose={this.createCommission}
                  commissionCategory={this.state.commissionCategory}
                  changeCommissionCategory={this.changeCommissionCategory}
                  modalType={this.state.modalType.toString()}
                  correlationId={this.state.correlationId}
                  openModal={this.openModal}
                  landlordProperties={landlordProperties}
                  commissionRooms={commissionRooms}
                />
              </If>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

PropertyCommission.propTypes = {
  t: PropTypes.func,
  match: PropTypes.object,
  property: PropTypes.object,
  getPropertyCommissionTiers: PropTypes.func,
  history: PropTypes.object,
  getPropertyStatus: PropTypes.string,
  commissionTiers: PropTypes.array,
  createStatus: PropTypes.string,
  updateStatus: PropTypes.string,
  deleteStatus: PropTypes.string,
  getLandlordProperties: PropTypes.func.isRequired,
  landlordProperties: PropTypes.array,
  bulkCreateStatus: PropTypes.string,
};

PropertyCommission.defaultProps = {
  match: {},
  history: {},
  property: {},
  t: () => {},
  getPropertyCommissionTiers: () => {},
  getPropertyStatus: '',
  commissionTiers: [],
  createStatus: '',
  updateStatus: '',
  deleteStatus: '',
  getLandlordProperties: () => {},
  landlordProperties: [],
  bulkCreateStatus: '',
};
